import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Package, MapPin, Clock, Loader2 } from 'lucide-react';
import leftoverService from '../services/leftoverService';
import { formatBeijingDate } from '../utils/dateUtils';

const LeftoverIndicator = ({ 
  productId, 
  leftoverCount, 
  leftoverTotalQuantity,
  onLeftoverSelect,
  excludeLeftoverIds = []
}) => {
  const [open, setOpen] = useState(false);
  const [leftovers, setLeftovers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLeftovers = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const response = await leftoverService.getByParentId(productId);
      setLeftovers(response.leftovers || []);
    } catch (error) {
      console.error('Failed to fetch leftovers:', error);
      setLeftovers([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (open) {
      fetchLeftovers();
    }
  }, [open, fetchLeftovers]);

  if (leftoverCount === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Badge 
          variant="secondary" 
          className="cursor-pointer hover:bg-secondary/80 flex items-center gap-1"
        >
          <Package className="h-3 w-3" />
          余料 {leftoverCount}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-96">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">可用余料</h4>
            <span className="text-xs text-muted-foreground">
              共 {leftoverTotalQuantity} 件
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : leftovers.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-sm">
              暂无可用余料
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">编号</TableHead>
                  <TableHead className="text-xs">类型</TableHead>
                  <TableHead className="text-xs">规格</TableHead>
                  <TableHead className="text-xs">库位</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leftovers
                  .filter(leftover => !excludeLeftoverIds.includes(leftover._id))
                  .map((leftover) => (
                  <TableRow 
                    key={leftover._id}
                    className={onLeftoverSelect ? "cursor-pointer hover:bg-muted" : ""}
                    onClick={() => onLeftoverSelect && onLeftoverSelect(leftover)}
                  >
                    <TableCell className="font-mono text-xs">
                      {leftover.code}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant={leftover.leftover_type === 'ground' ? 'default' : 'secondary'} className="text-[10px]">
                        {leftover.leftover_type === 'ground' ? '精磨' : '毛坯'}
                      </Badge>
                      {(leftover.source?.type === 'subcontract_return' || leftover.source?.type === '外协回料') && (
                        <span className="text-[10px] text-muted-foreground block">外协</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>Ø{leftover.tip_diameter}mm</div>
                      <div className="text-muted-foreground">
                        {leftover.length}mm × {leftover.quantity}件
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {leftover.location}
                      </div>
                      <div className="text-muted-foreground text-xs mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {formatBeijingDate(leftover.created_at)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {onLeftoverSelect && (
            <p className="text-xs text-muted-foreground">
              点击余料行可选择使用
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LeftoverIndicator;